import { groupItemsByCaseSizeRules, pluralize } from "@sellar-io/common";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "@sellar-io/components";

const ProgressBar = ({
  productName,
  icon,
  casesOrdered,
  caseQuantitySize,
}: {
  productName: string;
  icon: "CAN" | "BOTTLE";
  casesOrdered: number;
  caseQuantitySize: number;
}) => {
  const timeoutRef = useRef<number | undefined>(undefined);
  const prevCasesOrdered = useRef<number>(casesOrdered);
  const [progress, setProgress] = useState<number>(0);
  const [remainderItemCount, setRemainderItemCount] = useState<number>(0);

  useEffect(() => {
    const totalItems = casesOrdered * caseQuantitySize;
    const remainder = totalItems % caseQuantitySize;
    const orderQuantityIncreased = casesOrdered > prevCasesOrdered.current;

    if (remainder === 0 && orderQuantityIncreased) {
      setProgress(100);
      setRemainderItemCount(caseQuantitySize);
      timeoutRef.current = window.setTimeout(() => {
        setProgress(0);
        setRemainderItemCount(0);
      }, 2000);
    } else {
      setProgress(Math.round((remainder / caseQuantitySize) * 100));
      setRemainderItemCount(Math.round(remainder));
    }

    prevCasesOrdered.current = casesOrdered;

    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, [caseQuantitySize, casesOrdered]);

  return (
    <div
      className={twMerge(
        "relative w-full rounded-md border-2",
        progress ? "border-indigo-800" : "border-dashed border-zinc-300",
      )}
    >
      <div
        className={twMerge(
          "h-8 overflow-hidden rounded-sm bg-indigo-100 text-white",
        )}
        style={{ transition: "width 0.5s ease-in-out", width: `${progress}%` }}
      ></div>
      <div
        className={twMerge(
          "absolute inset-0 flex items-center justify-between p-1 text-xs",
          progress ? "text-indigo-700" : "text-zinc-700",
        )}
      >
        <div className="flex flex-wrap items-center">
          <div>
            <Icon size={1} icon={icon} />
          </div>
          <span className={progress ? "font-bold" : ""}>{productName}</span>
        </div>

        <b>
          {remainderItemCount === 0
            ? caseQuantitySize
            : `${remainderItemCount}/${caseQuantitySize}`}
        </b>
      </div>
    </div>
  );
};

const findOrderItem = (
  properties: {
    containerType: "can" | "bottle";
    unit: string;
    volume: number;
  },
  orderItemList: {
    quantity: number;
    containerType: string | null;
    volume: number | null;
    unit: string | null;
    packQuantity: number | null;
  }[],
) => {
  return orderItemList.find(
    (item) =>
      item.containerType === properties.containerType &&
      item.unit === properties.unit &&
      item.volume === properties.volume,
  );
};

const generateProductItemName = (productItemProperties: {
  containerType: string;
  volume: number;
  unit: string;
}) => {
  const { containerType, volume, unit } = productItemProperties;
  return `${volume}${unit} ${pluralize(containerType, 2)}`;
};

export const CaseSizeRuleSummary = ({
  orderItemList,
  caseSizeRuleList,
}: {
  orderItemList: {
    quantity: number;
    containerType: string | null;
    volume: number | null;
    unit: string | null;
    packQuantity: number | null;
  }[];
  caseSizeRuleList: {
    containerType: "can" | "bottle";
    unit: string;
    volume: number;
    quantity: number;
  }[];
}) => {
  if (!caseSizeRuleList.length) {
    return null;
  }

  const orderItemsGroupedByCaseSizeRules = groupItemsByCaseSizeRules({
    caseSizeRuleList,
    orderItemList,
  });

  const title = pluralize("case size", caseSizeRuleList.length);

  return (
    <div className="flex flex-col gap-y-2 py-2">
      <div className="text-sm font-semibold uppercase">{title}</div>

      {caseSizeRuleList.map((rule) => {
        const productName = generateProductItemName(rule);
        const key =
          rule.containerType + rule.quantity + rule.unit + rule.volume;
        const icon = rule.containerType === "can" ? "CAN" : "BOTTLE";
        const matchingOrderItem = findOrderItem(
          rule,
          orderItemsGroupedByCaseSizeRules,
        );

        return (
          <div key={key}>
            <ProgressBar
              productName={productName}
              icon={icon}
              casesOrdered={matchingOrderItem?.quantity ?? 0}
              caseQuantitySize={rule.quantity}
            />
          </div>
        );
      })}
    </div>
  );
};
